import React, { useState, useEffect } from 'react';
import { Ingrediente, StatisticheIngredientiResponse } from '../types';
import apiService, { CeFornitore } from '../services/api';
import GelatoDataGrid from '../components/DataGrid/GelatoDataGrid';
import { GridApi } from 'ag-grid-community';
import { Pencil, Lock, Unlock } from 'lucide-react';
import './IngredientiPage.css';

const IngredientiPage: React.FC = () => {
  const [ingredienti, setIngredienti] = useState<Ingrediente[]>([]);
  const [fornitori, setFornitori] = useState<CeFornitore[]>([]);
  const [statisticheData, setStatisticheData] = useState<StatisticheIngredientiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mostraForm, setMostraForm] = useState(false);
  const [mostraStatistiche, setMostraStatistiche] = useState(false);
  const [ingredienteCorrente, setIngredienteCorrente] = useState<Ingrediente | null>(null);
  const [includiDisattivi, setIncludiDisattivi] = useState(false);
  const [vistaTabella, setVistaTabella] = useState(false); // Nuovo stato per vista tabella
  
  // AG Grid state
  const [, setGridApi] = useState<GridApi | null>(null);
  
  // Stati per ordinamento e paginazione
  const [sortColumn] = useState<string>('nome');
  const [sortDirection] = useState<'asc' | 'desc'>('asc');
  const [, setCurrentPage] = useState<number>(1);
  
  // Filtri per le statistiche
  const [dataInizio, setDataInizio] = useState(() => {
    const annoCorrente = new Date().getFullYear();
    return `${annoCorrente}-01-01`;
  });
  const [dataFine, setDataFine] = useState(() => {
    const annoCorrente = new Date().getFullYear();
    return `${annoCorrente}-12-31`;
  });

  const [formData, setFormData] = useState({
    nome: '',
    descrizione: '',
    costo_per_unita: 0,
    unita_misura: 'kg' as 'kg' | 'l' | 'g' | 'ml' | 'pz',
    fornitore: '',
    fornitore_id: '' as number | '',
    calorie: 0,
    carboidrati: 0,
    zuccheri: 0,
    grassi: 0,
    proteine: 0,
    sale: 0,
    pac: 0,
    pod: 0,
    attivo: true
  });

  useEffect(() => {
    // L'anagrafica serve alla tendina del form: se non si riesce a leggerla la
    // pagina funziona lo stesso, resta solo senza fornitori fra cui scegliere.
    apiService.getFornitori({ soloAttivi: true })
      .then(setFornitori)
      .catch(() => setFornitori([]));
  }, []);

  useEffect(() => {
    caricaIngredienti();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ricarica solo al toggle dei disattivi; caricaIngredienti è ricreata ad ogni render
  }, [includiDisattivi]);

  const caricaIngredienti = async () => {
    try {
      setLoading(true);
      const data = await apiService.getIngredienti(includiDisattivi);
      setIngredienti(data);
      setCurrentPage(1); // Reset alla prima pagina quando si caricano nuovi dati
    } catch (err) {
      setError('Errore nel caricamento degli ingredienti');
    } finally {
      setLoading(false);
    }
  };
  
  // Ordina gli ingredienti in base alla colonna e direzione selezionate
  const sortedIngredienti = [...ingredienti].sort((a, b) => {
    const multiplier = sortDirection === 'asc' ? 1 : -1;
    
    switch (sortColumn) {
      case 'nome':
        return multiplier * a.nome.localeCompare(b.nome);
      case 'fornitore':
        return multiplier * (a.fornitore || '').localeCompare(b.fornitore || '');
      case 'costo':
        return multiplier * (a.costo_per_unita - b.costo_per_unita);
      default:
        return 0;
    }
  });
  
  // AG Grid functions
  const onGridReady = (params: any) => {
    setGridApi(params.api);
  };

  const onCellClicked = async (params: any) => {
    if (params.colDef.field === 'actions') {
      const target = params.event.target;
      const ingrediente = params.data;
      
      if (target.classList.contains('btn-edit')) {
        handleModifica(ingrediente);
      } else if (target.classList.contains('btn-toggle')) {
        handleToggleAttivo(ingrediente.id, !ingrediente.attivo);
      }
    }
  };

  // AG Grid column definitions
  const columnDefs = [
    {
      headerName: 'Nome',
      field: 'nome',
      sortable: true,
      filter: true,
      flex: 2,
      cellRenderer: (params: any) => {
        const ingrediente = params.data;
        return React.createElement('div', {
          style: { display: 'flex', alignItems: 'center', gap: '8px' }
        }, [
          React.createElement('div', { key: 'info', style: { display: 'flex', flexDirection: 'column' } }, [
            React.createElement('strong', { key: 'nome' }, ingrediente.nome),
            ingrediente.descrizione && React.createElement('small', { 
              key: 'desc', 
              style: { color: '#666', fontSize: '0.85em' } 
            }, ingrediente.descrizione)
          ])
        ]);
      }
    },
    {
      headerName: 'Unità/Costo',
      field: 'unita_costo',
      sortable: true,
      filter: true,
      flex: 1.5,
      cellRenderer: (params: any) => {
        const ingrediente = params.data;
        return React.createElement('div', {
          style: { display: 'flex', flexDirection: 'column' }
        }, [
          React.createElement('span', {
            key: 'unita',
            style: { fontWeight: 'bold' }
          }, ingrediente.unita_misura),
          React.createElement('span', {
            key: 'costo',
            style: { color: '#666', fontSize: '0.9em' }
          }, `€${ingrediente.costo_per_unita.toFixed(2)}/${ingrediente.unita_misura}`)
        ]);
      }
    },
    {
      headerName: 'Fornitore',
      field: 'fornitore',
      sortable: true,
      filter: true,
      flex: 1.5,
      cellRenderer: (params: any) => {
        const fornitore = params.value;
        return fornitore || React.createElement('span', { style: { color: '#999', fontStyle: 'italic' } }, 'Non specificato');
      }
    },
    {
      headerName: 'Valori Nutrizionali',
      field: 'nutritional',
      sortable: false,
      filter: false,
      flex: 2,
      cellRenderer: (params: any) => {
        const ingrediente = params.data;
        const values = [];
        
        if (ingrediente.calorie > 0) {
          values.push(`${ingrediente.calorie} kcal`);
        }
        if (ingrediente.pac > 0) {
          values.push(`PAC: ${ingrediente.pac.toFixed(1)}`);
        }
        if (ingrediente.pod > 0) {
          values.push(`POD: ${ingrediente.pod.toFixed(1)}`);
        }
        
        return React.createElement('div', {
          style: { fontSize: '0.85em', color: '#666' }
        }, values.length > 0 ? values.join(' • ') : 'N/A');
      }
    },
    {
      headerName: 'Stato',
      field: 'attivo',
      sortable: true,
      filter: true,
      flex: 1,
      cellRenderer: (params: any) => {
        const isActive = params.value;
        return React.createElement('span', {
          className: `badge ${isActive ? 'badge-success' : 'badge-danger'}`
        }, isActive ? 'Attivo' : 'Inattivo');
      }
    },
    {
      headerName: 'Azioni',
      field: 'actions',
      width: 120,
      sortable: false,
      filter: false,
      pinned: 'right' as const,
      cellRenderer: (params: any) => {
        const buttons = [
          React.createElement('button', {
            className: 'btn-edit',
            title: 'Modifica ingrediente',
            key: 'edit'
          }, React.createElement(Pencil, { size: 16, style: { pointerEvents: 'none' } })),
          React.createElement('button', {
            className: `btn-toggle ${params.data.attivo ? 'btn-warning' : 'btn-success'}`,
            title: params.data.attivo ? 'Disattiva ingrediente' : 'Attiva ingrediente',
            key: 'toggle'
          }, React.createElement(params.data.attivo ? Lock : Unlock, { size: 16, style: { pointerEvents: 'none' } }))
        ];

        return React.createElement('div', {
          className: 'grid-actions-cell',
          'data-row-id': params.data.id
        }, buttons);
      }
    }
  ];

  // AG Grid column definitions per le statistiche
  const statisticheColumnDefs = [
    {
      headerName: 'Ingrediente',
      field: 'nome',
      sortable: true,
      filter: true,
      flex: 2,
      cellClass: (params: any) => params.node.rowPinned ? 'ag-pinned-row-total' : '',
      cellStyle: (params: any) => params.node.rowPinned ? { fontWeight: 'bold', fontSize: '1.1em' } : undefined
    },
    {
      headerName: 'Fornitore',
      field: 'fornitore',
      sortable: true,
      filter: true,
      flex: 1.5,
      cellRenderer: (params: any) => {
        if (params.node.rowPinned) return '';
        return params.value || React.createElement('span', { style: { color: '#999', fontStyle: 'italic' } }, 'Non specificato');
      }
    },
    {
      headerName: 'Gusti Utilizzati',
      field: 'gusti_utilizzati',
      sortable: true,
      filter: true,
      flex: 1,
      cellClass: (params: any) => params.node.rowPinned ? 'ag-pinned-row-total' : ''
    },
    {
      headerName: 'Kg Gelato Prodotti',
      field: 'kg_prodotti_totali',
      sortable: true,
      filter: true,
      flex: 1.5,
      cellRenderer: (params: any) => {
        if (params.value === null || params.value === undefined) return '';
        return `${Number(params.value).toFixed(1)} kg`;
      },
      cellClass: (params: any) => params.node.rowPinned ? 'ag-pinned-row-total' : '',
      cellStyle: (params: any) => params.node.rowPinned ? { fontWeight: 'bold' } : undefined
    },
    {
      headerName: 'Quantità Utilizzata',
      field: 'quantita_utilizzata_kg',
      sortable: true,
      filter: true,
      flex: 1.5,
      cellRenderer: (params: any) => {
        if (params.value === null || params.value === undefined) return '';
        return `${Number(params.value).toFixed(2)} kg`;
      },
      cellClass: (params: any) => params.node.rowPinned ? 'ag-pinned-row-total' : '',
      cellStyle: (params: any) => params.node.rowPinned ? { fontWeight: 'bold' } : undefined
    },
    {
      headerName: 'Costo per Unità',
      field: 'costo_per_unita',
      sortable: true,
      filter: true,
      flex: 1.2,
      cellRenderer: (params: any) => {
        if (params.value === null || params.value === undefined || params.node.rowPinned) return '';
        return `€${Number(params.value).toFixed(2)}/${params.data.unita_misura}`;
      }
    },
    {
      headerName: 'Costo Totale',
      field: 'costo_totale_utilizzato',
      sortable: true,
      filter: true,
      flex: 1.2,
      cellRenderer: (params: any) => {
        if (params.value === null || params.value === undefined) return '';
        return `€${Number(params.value).toFixed(2)}`;
      },
      cellClass: (params: any) => params.node.rowPinned ? 'ag-pinned-row-total' : '',
      cellStyle: (params: any) => params.node.rowPinned ? { fontWeight: 'bold', fontSize: '1.1em', color: '#d32f2f' } : undefined
    }
  ];


  const caricaStatistiche = async (customDataInizio?: string, customDataFine?: string) => {
    try {
      const inizioFiltro = customDataInizio || dataInizio;
      const fineFiltro = customDataFine || dataFine;
      
      const data = await apiService.getStatisticheIngredienti(inizioFiltro, fineFiltro);
      setStatisticheData(data);
      setMostraStatistiche(true);
    } catch (err) {
      setError('Errore nel caricamento delle statistiche');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      descrizione: '',
      costo_per_unita: 0,
      unita_misura: 'kg',
      fornitore: '',
      fornitore_id: '',
      calorie: 0,
      carboidrati: 0,
      zuccheri: 0,
      grassi: 0,
      proteine: 0,
      sale: 0,
      pac: 0,
      pod: 0,
      attivo: true
    });
    setIngredienteCorrente(null);
    setMostraForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      
      if (ingredienteCorrente) {
        await apiService.aggiornaIngrediente(ingredienteCorrente.id, formData);
      } else {
        await apiService.creaIngrediente(formData);
      }
      await caricaIngredienti();
      resetForm();
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      setError('Errore nel salvataggio dell\'ingrediente');
    }
  };

  const handleModifica = (ingrediente: Ingrediente) => {
    setFormData({
      nome: ingrediente.nome,
      descrizione: ingrediente.descrizione || '',
      costo_per_unita: ingrediente.costo_per_unita,
      unita_misura: ingrediente.unita_misura,
      fornitore: ingrediente.fornitore,
      fornitore_id: (ingrediente as any).fornitore_id || '',
      calorie: ingrediente.calorie || 0,
      carboidrati: ingrediente.carboidrati || 0,
      zuccheri: ingrediente.zuccheri || 0,
      grassi: ingrediente.grassi || 0,
      proteine: ingrediente.proteine || 0,
      sale: ingrediente.sale || 0,
      pac: ingrediente.pac || 0,
      pod: ingrediente.pod || 0,
      attivo: ingrediente.attivo
    });
    setIngredienteCorrente(ingrediente);
    setMostraForm(true);
  };

  const handleToggleAttivo = async (id: number, attivo: boolean) => {
    try {
      if (attivo) {
        await apiService.attivaIngrediente(id);
      } else {
        await apiService.disattivaIngrediente(id);
      }
      await caricaIngredienti();
    } catch (err) {
      setError('Errore nell\'aggiornamento dello stato dell\'ingrediente');
    }
  };

  if (loading) {
    return <div className="loading">Caricamento ingredienti...</div>;
  }

  return (
    <div className="ingredienti-page">
      <div className="page-header">
        <h1>Gestione Ingredienti</h1>
        <div className="header-actions">
          <button
            onClick={() => setMostraForm(true)}
            className="btn btn-primary"
          >
            Nuovo Ingrediente
          </button>
          <button
            onClick={() => caricaStatistiche()}
            className="btn btn-secondary"
          >
            Visualizza Statistiche
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="btn-close">×</button>
        </div>
      )}

      <div className="filters">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={includiDisattivi}
            onChange={(e) => setIncludiDisattivi(e.target.checked)}
          />
          Includi ingredienti disattivati
        </label>
        
        <div className="view-toggle">
          <span className="toggle-label">Vista:</span>
          <button
            className={`btn-toggle ${!vistaTabella ? 'active' : ''}`}
            onClick={() => setVistaTabella(false)}
          >
            Schede
          </button>
          <button
            className={`btn-toggle ${vistaTabella ? 'active' : ''}`}
            onClick={() => setVistaTabella(true)}
          >
            Tabella
          </button>
        </div>
      </div>

      {mostraForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{ingredienteCorrente ? 'Modifica Ingrediente' : 'Nuovo Ingrediente'}</h2>
              <button onClick={resetForm} className="btn-close">×</button>
            </div>
            <form onSubmit={handleSubmit} className="ingrediente-form">
              <div className="form-group">
                <label>Nome *</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  required
                  placeholder="Es. Zucchero bianco"
                />
              </div>

              <div className="form-group">
                <label>Descrizione</label>
                <textarea
                  value={formData.descrizione}
                  onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                  placeholder="Descrizione dell'ingrediente"
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Costo per unità (€) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.costo_per_unita}
                    onChange={(e) => setFormData({...formData, costo_per_unita: parseFloat(e.target.value) || 0})}
                    required
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label>Unità di misura *</label>
                  <select
                    value={formData.unita_misura}
                    onChange={(e) => setFormData({...formData, unita_misura: e.target.value as 'kg' | 'l' | 'g' | 'ml' | 'pz'})}
                    required
                  >
                    <option value="kg">Kg</option>
                    <option value="l">Litri</option>
                    <option value="g">Grammi</option>
                    <option value="ml">Millilitri</option>
                    <option value="pz">Pezzi</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Fornitore</label>
                <select
                  value={formData.fornitore_id}
                  onChange={(e) => setFormData({
                    ...formData,
                    fornitore_id: e.target.value ? Number(e.target.value) : ''
                  })}
                >
                  <option value="">Nessuno</option>
                  {fornitori.map(f => (
                    <option key={f.id} value={f.id}>{f.nome}</option>
                  ))}
                </select>
                {/* Il nome scritto a mano prima che l'anagrafica esistesse resta
                    visibile finche' non e' abbinato: e' la traccia da cui si
                    capisce cosa intendeva chi ha compilato la scheda. */}
                {!formData.fornitore_id && formData.fornitore && (
                  <small style={{ color: 'var(--text-secondary)' }}>
                    Prima era scritto: <strong>{formData.fornitore}</strong> — da abbinare
                    in Anagrafiche &rsaquo; Fornitori
                  </small>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Calorie (kcal)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.calorie}
                    onChange={(e) => setFormData({ ...formData, calorie: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label>Carboidrati (g)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.carboidrati}
                    onChange={(e) => setFormData({ ...formData, carboidrati: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Zuccheri (g)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.zuccheri}
                    onChange={(e) => setFormData({ ...formData, zuccheri: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label>Grassi (g)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.grassi}
                    onChange={(e) => setFormData({ ...formData, grassi: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Proteine (g)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.proteine}
                  onChange={(e) => setFormData({ ...formData, proteine: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div className="form-group">
                <label>Sale (g)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.sale}
                  onChange={(e) => setFormData({ ...formData, sale: parseFloat(e.target.value) || 0 })}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>PAC (Potere Anti-Congelante)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.pac}
                    onChange={(e) => setFormData({ ...formData, pac: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label>POD (Potere Dolcificante)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.pod}
                    onChange={(e) => setFormData({ ...formData, pod: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.attivo}
                    onChange={(e) => setFormData({...formData, attivo: e.target.checked})}
                  />
                  Ingrediente attivo
                </label>
              </div>

              <div className="form-actions">
                <button type="button" onClick={resetForm} className="btn btn-secondary">
                  Annulla
                </button>
                <button type="submit" className="btn btn-primary">
                  {ingredienteCorrente ? 'Aggiorna' : 'Crea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {mostraStatistiche && (
        <div className="modal-overlay">
          <div className="modal modal-xl">
            <div className="modal-header">
              <h2>Statistiche Ingredienti</h2>
              <button onClick={() => setMostraStatistiche(false)} className="btn-close">×</button>
            </div>
            
            {/* Filtri per le date */}
            <div className="filtri-statistiche">
              <div className="filtri-row">
                <div className="filtro-group">
                  <label htmlFor="data-inizio">Data Inizio:</label>
                  <input
                    type="date"
                    id="data-inizio"
                    value={dataInizio}
                    onChange={(e) => setDataInizio(e.target.value)}
                  />
                </div>
                <div className="filtro-group">
                  <label htmlFor="data-fine">Data Fine:</label>
                  <input
                    type="date"
                    id="data-fine"
                    value={dataFine}
                    onChange={(e) => setDataFine(e.target.value)}
                  />
                </div>
                <button
                  onClick={() => caricaStatistiche(dataInizio, dataFine)}
                  className="btn btn-primary"
                >
                  Aggiorna Statistiche
                </button>
              </div>
            </div>

            {statisticheData && (
              <>
                {/* Riepilogo del periodo */}
                <div className="periodo-info">
                  <h3>Periodo: {statisticheData.periodo.data_inizio} - {statisticheData.periodo.data_fine}</h3>
                  <div className="totali-grid">
                    <div className="totale-card">
                      <span className="totale-label">Ingredienti Analizzati</span>
                      <span className="totale-valore">{statisticheData.totali.ingredienti_analizzati}</span>
                    </div>
                    <div className="totale-card">
                      <span className="totale-label">Kg Gelato Prodotti</span>
                      <span className="totale-valore">{statisticheData.totali.kg_totali_prodotti.toFixed(1)} kg</span>
                    </div>
                    <div className="totale-card">
                      <span className="totale-label">Ingredienti Utilizzati</span>
                      <span className="totale-valore">{statisticheData.totali.quantita_totale_utilizzata.toFixed(1)} kg</span>
                    </div>
                    <div className="totale-card">
                      <span className="totale-label">Costo Totale</span>
                      <span className="totale-valore">€{statisticheData.totali.costo_totale.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Statistiche dettagliate - Tabella AG Grid */}
                <div className="statistiche-container">
                  {statisticheData.statistiche.length > 0 ? (
                    <div className="ag-grid-container">
                      <GelatoDataGrid
                        rowData={statisticheData.statistiche}
                        columnDefs={statisticheColumnDefs}
                        pagination={false}
                        pinnedBottomRowData={[{
                          nome: 'TOTALE',
                          gusti_utilizzati: '',
                          kg_prodotti_totali: statisticheData.totali.kg_totali_prodotti,
                          quantita_utilizzata_kg: statisticheData.totali.quantita_totale_utilizzata,
                          costo_per_unita: null,
                          costo_totale_utilizzato: statisticheData.totali.costo_totale,
                          costo_medio_effettivo: null,
                          fornitore: '',
      fornitore_id: '',
                          unita_misura: ''
                        }]}
                        height="600px"
                        suppressCellFocus={true}
                      />
                    </div>
                  ) : (
                    <div className="no-stats">Nessuna statistica disponibile per il periodo selezionato</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="ingredienti-list">
        {ingredienti.length > 0 ? (
          vistaTabella ? (
            // Vista Tabella con AG Grid
            <div className="ag-grid-container">
              <GelatoDataGrid
                rowData={sortedIngredienti}
                columnDefs={columnDefs}
                onGridReady={onGridReady}
                onCellClicked={onCellClicked}
                pagination={true}
                paginationPageSize={20}
                height="600px"
              />
            </div>
          ) : (
            // Vista Schede (default)
            <div className="card-grid">
              {ingredienti.map(ingrediente => (
                <div key={ingrediente.id} className={`item-card ${!ingrediente.attivo ? 'inactive' : ''}`}>
                  <div className="item-card-header">
                    <div className="ingrediente-title">
                      <h3>{ingrediente.nome}</h3>
                    </div>
                    <span className={`status-badge ${ingrediente.attivo ? 'active' : 'inactive'}`}>
                      {ingrediente.attivo ? 'Attivo' : 'Disattivo'}
                    </span>
                  </div>
                  
                  <div className="item-card-body">
                    <span className="category-badge">{ingrediente.unita_misura}</span>
                    {ingrediente.descrizione && (
                      <p className="description">{ingrediente.descrizione}</p>
                    )}
                    
                    {ingrediente.fornitore && (
                      <div className="supplier-info">
                        <h4 className="supplier-heading">Fornitore:</h4>
                        <p className="supplier-name">
                          {(ingrediente as any).fornitore_nome || ingrediente.fornitore}
                        </p>
                      </div>
                    )}
                    
                    <div className="price-info">
                      <span className="price-label">Costo:</span>
                      <span className="price-value">€{ingrediente.costo_per_unita.toFixed(2)}/{ingrediente.unita_misura}</span>
                    </div>
                    
                    {/* Metriche nutrizionali */}
                    <div className="ingrediente-metrics-enhanced">
                      <div className="metric-box">
                        <span className="metric-label">Kcal</span>
                        <span className="metric-value">
                          {ingrediente.calorie ? `${ingrediente.calorie}` : '-'}
                        </span>
                      </div>
                      <div className="metric-box">
                        <span className="metric-label">Carb</span>
                        <span className="metric-value">
                          {ingrediente.carboidrati ? `${ingrediente.carboidrati}g` : '-'}
                        </span>
                      </div>
                      <div className="metric-box">
                        <span className="metric-label">Prot</span>
                        <span className="metric-value">
                          {ingrediente.proteine ? `${ingrediente.proteine}g` : '-'}
                        </span>
                      </div>
                      <div className="metric-box">
                        <span className="metric-label">Grassi</span>
                        <span className="metric-value">
                          {ingrediente.grassi ? `${ingrediente.grassi}g` : '-'}
                        </span>
                      </div>
                      <div className="metric-box">
                        <span className="metric-label">Zucch</span>
                        <span className="metric-value">
                          {ingrediente.zuccheri && ingrediente.zuccheri > 0 ? `${ingrediente.zuccheri}g` : '-'}
                        </span>
                      </div>
                      <div className="metric-box">
                        <span className="metric-label">Sale</span>
                        <span className="metric-value">
                          {ingrediente.sale && ingrediente.sale > 0 ? `${ingrediente.sale}g` : '-'}
                        </span>
                      </div>
                      <div className="metric-box">
                        <span className="metric-label">PAC</span>
                        <span className="metric-value">
                          {ingrediente.pac && ingrediente.pac > 0 ? ingrediente.pac : '-'}
                        </span>
                      </div>
                      <div className="metric-box">
                        <span className="metric-label">POD</span>
                        <span className="metric-value">
                          {ingrediente.pod && ingrediente.pod > 0 ? ingrediente.pod : '-'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="card-actions">
                      <button
                        onClick={() => handleModifica(ingrediente)}
                        className="btn btn-sm btn-secondary"
                      >
                        Modifica
                      </button>
                      <button
                        onClick={() => handleToggleAttivo(ingrediente.id, !ingrediente.attivo)}
                        className={`btn btn-sm ${ingrediente.attivo ? 'btn-warning' : 'btn-success'}`}
                      >
                        {ingrediente.attivo ? 'Disattiva' : 'Attiva'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="no-ingredienti">
            <p>Nessun ingrediente trovato.</p>
            <button onClick={() => setMostraForm(true)} className="btn btn-primary">
              Crea il primo ingrediente
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default IngredientiPage;
